import { css } from '@emotion/css';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../features';
import { actions, ReactionState } from '../../features/reaction';
import { sendEvent } from '../../lib/google-analytics';
import { ReactionItem } from '../../models/ReactionItem';
import { EmptyCell } from './EmptyCell';
import { ReactionCell } from './ReactionCell';

interface ReactionHistoryProps {
  items: ReactionItem[];
  isSending?: boolean;
}

// 履歴の表示数
const numberOfHistory = 5;

export const ReactionHistory: React.FC<ReactionHistoryProps> = ({ items, isSending }) => {
  const { t } = useTranslation('reaction');
  const { userReactionStates, selectedReaction } = useSelector(
    (state: RootState) => state.reaction
  );
  const { selectedUserId } = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();

  const handleClick = (item: ReactionItem) => () => {
    if (selectedReaction?.place === 'history' && item.label === selectedReaction?.label) {
      dispatch(actions.postReaction(selectedReaction));
      sendEvent('reaction_send_reaction_icon', 'submit', item.label);
    } else {
      dispatch(actions.selectReaction({ ...item, place: 'history' }));
    }
  };

  const historyItems = getHistoryItems(selectedUserId, userReactionStates, items);
  const blankCount = Math.max(0, numberOfHistory - historyItems.length);
  const blanks = [...Array(blankCount).map(() => undefined)];

  return (
    <div
      className={css`
        padding: 12px 20px 8px;
        .content {
          overflow: hidden;
        }
        h3 {
          font-size: 14px;
        }
        ul {
          padding: 12px 0 0;
          list-style-type: none;
          white-space: nowrap;
        }
      `}
    >
      <div className="content">
        <h3>{t('recently_used_reactions')}</h3>
        <ul>
          {[...historyItems, ...blanks].map((item, i) =>
            item ? (
              <ReactionCell
                key={item.label}
                src={item.url}
                name={item.name}
                onClick={handleClick(item)}
                isSelected={
                  selectedReaction?.place === 'history' && item.label === selectedReaction?.label
                }
                isSending={isSending}
              />
            ) : (
              <EmptyCell key={`empty-${i}`} />
            )
          )}
        </ul>
      </div>
    </div>
  );
};

function getHistoryItems(
  userId: string | undefined,
  userReactionStates: ReactionState['userReactionStates'],
  reactions: ReactionItem[]
): ReactionItem[] {
  if (!userId) {
    return [];
  }

  const userReactionState = (userReactionStates ?? {})[userId] || { history: [] };
  const sliced = userReactionState.history.slice(0, numberOfHistory);
  const historyItems: ReactionItem[] = [];
  sliced.forEach(item => {
    const found = reactions.find(r => item.label === r.label);
    if (found) {
      historyItems.push(found);
    }
  });
  return historyItems;
}
